/**
 * Top navigation — flat, professional, B2B SaaS style (Stripe / GitHub).
 *
 * Per founder direction (2026-05):
 *   - No "pill / bubble" chrome — text tabs with an underline on the active one
 *   - Real horizontal logo on the left (not a tiny text logo)
 *   - "Hi, John ▾" account menu on the right (see AccountMenu.jsx)
 *   - No floating "k" chip — that confused users. Board context is shown
 *     inside each board-scoped page, not in the nav.
 *
 * Tab list mirrors the founder's spec:
 *   - Pick a board (renames to "Create a board" copy-wise but path stays
 *     /pick-board for now to avoid breaking existing deep links)
 *   - Manage Board    (board-scoped — deep-links via selectedBoardId)
 *   - AI Coach
 *   - Ready Boards    (paid-tier locked, badged)
 *   - Therapist
 *   - Schools
 *   - About us
 *   - Contact us
 *
 * When a board-scoped tab is clicked without a selected board, the nav
 * redirects to /pick-board with a sessionStorage hint surfaced as a
 * banner there.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getSelectedBoardId, useSelectedBoard } from "@/lib/selectedBoard";
import { api } from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import NotificationsBell from "@/components/NotificationsBell";

const HIDDEN_PATHS = new Set([
  "/", "/login", "/register", "/forgot-password", "/reset-password",
  "/pair-device", "/consent/parental/verify", "/billing/success",
  "/about", "/community", "/press", "/download", "/pricing",
  "/therapists", "/built-in-a-day", "/demo-safety", "/waitlist", "/whats-new",
  "/about-us", "/contact", "/security", "/data-protection", "/learn-and-grow",
]);

const PICK_REDIRECT_KEY = "sonspeak_picker_redirect_v1";

const TABS = [
  { id: "pick-board",    label: "Boards",      to: "/pick-board",      scoped: false },
  { id: "manage-board",  label: "Manage Board", to: null,              scoped: true,
    href: (bid) => `/manage/${bid}?tab=create-button` },
  { id: "ai-coach",      label: "AI Coach",    to: "/coach",           scoped: false },
  { id: "ready-boards",  label: "Ready Boards", to: "/ready-boards",   scoped: false, badge: "Soon" },
  { id: "therapist",     label: "Therapist",   to: "/therapist-portal", scoped: false },
  { id: "schools",       label: "Schools",     to: "/schools-portal",  scoped: false },
  { id: "about-us",      label: "About",       to: "/about-us",        scoped: false },
  { id: "contact",       label: "Contact",     to: "/contact",         scoped: false },
];

export default function TopNav() {
  const location = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAuthenticated = !!user && user !== false;
  const [boardId] = useSelectedBoard();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close the mobile drawer on route change so it doesn't stick
  // open when a tab is picked from inside it.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Auto-pick the only board if the user has just one. Saves the user
  // a click and keeps board-scoped tabs from bouncing to /pick-board.
  useEffect(() => {
    if (!isAuthenticated || boardId) return;
    let cancelled = false;
    api.get("/profiles").then((r) => {
      if (cancelled) return;
      const list = r.data?.profiles || r.data || [];
      if (Array.isArray(list) && list.length === 1) {
        try {
          localStorage.setItem("sonspeak_selected_board_v1", list[0].id);
          window.dispatchEvent(new CustomEvent("sonspeak:selected-board", { detail: { id: list[0].id } }));
        } catch { /* noop */ }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated, boardId]);

  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);

  if (!isAuthenticated) return null;
  if (HIDDEN_PATHS.has(location.pathname)) return null;
  if (location.pathname.startsWith("/board/")) return null;
  if (location.pathname.startsWith("/first-then/") || location.pathname.startsWith("/choice/")) return null;
  if (location.pathname.startsWith("/admin/")) return null;

  const onTabClick = (e, t) => {
    if (!t.scoped) return;
    const bid = boardId || getSelectedBoardId();
    if (!bid) {
      e.preventDefault();
      try { sessionStorage.setItem(PICK_REDIRECT_KEY, t.id); } catch { /* noop */ }
      nav("/pick-board");
    }
  };

  const isActive = (t) => {
    const p = location.pathname;
    if (t.id === "pick-board") return p === "/pick-board" || p === "/profiles";
    if (t.id === "manage-board") return p.startsWith("/manage/");
    if (t.id === "ai-coach") return p === "/coach" || p.startsWith("/coach/");
    if (t.id === "ready-boards") return p.startsWith("/ready-boards");
    if (t.id === "therapist") return p === "/therapist-portal" || p === "/clients";
    if (t.id === "schools") return p === "/schools-portal";
    if (t.id === "about-us") return p === "/about-us";
    if (t.id === "contact") return p === "/contact";
    return false;
  };

  return (
    <nav
      className="sticky top-0 z-30 bg-[color:var(--paper)] border-b border-[color:var(--ink)]/15"
      data-testid="top-nav"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-3 sm:gap-6">
        {/* Logo — sized down here (hero placement is on Landing). 36px lockup. */}
        <Link to="/pick-board" className="flex items-center shrink-0" data-testid="top-nav-logo">
          <img
            src="/brand/sonspeak-logo-transparent.png"
            alt="SonSpeak"
            className="h-8 sm:h-10 w-auto"
          />
        </Link>

        {/* Mobile/tablet hamburger — collapses the tab list below lg so
            the pills don't overlap on iPad-portrait width (~820px). Above
            lg the regular inline tabs render as before. */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden ml-auto inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-[color:var(--ink)]/5 transition"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="top-nav-mobile-sheet"
          data-testid="top-nav-mobile-toggle"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        {/* Flat text tabs — hidden below lg (tablet-portrait uses sheet) */}
        <div className="hidden lg:flex items-center gap-5 overflow-x-auto flex-1" role="tablist">
          {TABS.map((t) => {
            const href = t.scoped ? (boardId ? t.href(boardId) : "/pick-board") : t.to;
            const active = isActive(t);
            return (
              <Link
                key={t.id}
                to={href}
                onClick={(e) => onTabClick(e, t)}
                aria-current={active ? "page" : undefined}
                role="tab"
                className={`shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold transition relative py-2 ${
                  active ? "text-[color:var(--ink)]" : "text-[color:var(--ink)]/70 hover:text-[color:var(--ink)]"
                }`}
                data-testid={`top-nav-tab-${t.id}`}
              >
                <span>{t.label}</span>
                {t.badge && (
                  <span className="text-[9px] uppercase tracking-[0.16em] font-bold bg-[color:var(--gold)] border border-[color:var(--ink)] px-1.5 py-0.5 rounded">
                    {t.badge}
                  </span>
                )}
                {active && (
                  <span
                    aria-hidden
                    className="absolute left-0 right-0 -bottom-px h-[3px] bg-[color:var(--coral)] rounded-full"
                  />
                )}
              </Link>
            );
          })}
        </div>

        <div className="hidden lg:block">
          <AccountMenu rightAddon={<NotificationsBell />} />
        </div>
      </div>

      {/* Mobile / tablet drawer sheet */}
      {mobileOpen && (
        <div
          id="top-nav-mobile-sheet"
          className="lg:hidden border-t border-[color:var(--ink)]/15 bg-[color:var(--paper)]"
          data-testid="top-nav-mobile-sheet"
        >
          <ul className="divide-y divide-[color:var(--ink)]/10 px-3 sm:px-6">
            {TABS.map((t) => {
              const href = t.scoped ? (boardId ? t.href(boardId) : "/pick-board") : t.to;
              const active = isActive(t);
              return (
                <li key={t.id}>
                  <Link
                    to={href}
                    onClick={(e) => onTabClick(e, t)}
                    aria-current={active ? "page" : undefined}
                    className={`flex items-center justify-between py-3 text-base font-semibold transition ${
                      active
                        ? "text-[color:var(--coral)]"
                        : "text-[color:var(--ink)] hover:text-[color:var(--coral)]"
                    }`}
                    data-testid={`top-nav-mobile-tab-${t.id}`}
                  >
                    <span>{t.label}</span>
                    {t.badge && (
                      <span className="text-[10px] uppercase tracking-[0.16em] font-bold bg-[color:var(--gold)] border border-[color:var(--ink)] px-2 py-0.5 rounded">
                        {t.badge}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-[color:var(--ink)]/10 px-3 sm:px-6 py-3 flex items-center justify-end">
            <AccountMenu rightAddon={<NotificationsBell />} />
          </div>
        </div>
      )}
    </nav>
  );
}

export { PICK_REDIRECT_KEY };
